import type { FindingAggregateResponseStatistics } from '../../utils/api-client/api-schemas.ts'
import { getEmptyResolutionCounts, type ResolutionCounts, type ResolutionPanelData } from './resolution-taxonomy.ts'

/**
 * Turns the API's finding statistics into the five resolution states the panel draws.
 *
 * The API has no single field for any of them, so each is derived. Two derivations rest on
 * assumptions worth knowing before trusting the numbers:
 *
 * 1. `fixed` is not in the statistics at all — there is no merged-patch count. The caller
 *    supplies `mergedCount` from a separate findings query filtered to `patch-status: merged`.
 * 2. `fixReady` uses `composite.ready_to_fix`, which the API does not guarantee is restricted
 *    to true-positive findings. It is clamped to what is left of the validated set so the five
 *    states always partition the analyzed set rather than double-counting.
 *
 * Not-triaged is the remainder rather than a sum of its outcomes, matching the convention in
 * `composition-utils.ts`: it absorbs findings sitting in inconclusive, blocked, excluded, error,
 * and not-yet-triaged states, which is also how the design groups them.
 */
export const toResolutionPanelData = ({
  statistics,
  mergedCount,
  notAnalyzedStatistics,
}: {
  /**
   * Statistics of the analyzed findings in view — a query sent with `analyzed: true`, the same
   * predicate the findings table filters by — or `undefined` where the view holds none (the
   * Unanalyzed quick view). Not-triaged is the remainder of this set's `total`, so the five states
   * add up to exactly the findings the table shows when filtered to analyzed findings.
   */
  statistics: FindingAggregateResponseStatistics | undefined
  mergedCount: number
  /**
   * Statistics of the findings not yet fully analyzed — a query sent with `analyzed: false`, the
   * complement of `statistics`. Its in-progress count splits it into In progress and Unanalyzed.
   * Taken from a query rather than the scan lifecycle state, which carries no statistics before
   * analysis has started.
   */
  notAnalyzedStatistics: Pick<FindingAggregateResponseStatistics, 'total' | 'in_progress_analysis'>
}): ResolutionPanelData => {
  const inProgressCount = Math.min(notAnalyzedStatistics.in_progress_analysis, notAnalyzedStatistics.total)
  const unanalyzedCount = notAnalyzedStatistics.total - inProgressCount

  if (!statistics) {
    return { resolution: getEmptyResolutionCounts(), inProgressCount, unanalyzedCount }
  }

  const { triage, composite, total } = statistics

  const dismissed = triage.outcome.false_positive + triage.outcome.wont_fix
  // Validated in the design's sense: true positives that warrant remediation. Fixed findings
  // start here too, then split out below.
  const validatedOrFixed = triage.outcome.true_positive + triage.outcome.likely_true_positive
  const untriaged = Math.max(total - dismissed - validatedOrFixed, 0)

  const fixed = Math.min(mergedCount, validatedOrFixed)
  // `ready_to_fix` counts every true positive with a completed fix, including those whose fix has
  // already merged, so the fixed findings are taken back out before it counts as fix-ready.
  const fixReady = Math.min(Math.max(composite.ready_to_fix - fixed, 0), validatedOrFixed - fixed)
  const acknowledged = validatedOrFixed - fixed - fixReady

  const resolution: ResolutionCounts = { fixed, fixReady, acknowledged, untriaged, dismissed }

  return { resolution, inProgressCount, unanalyzedCount }
}
